/**
 * Service for managing chat history storage using IndexedDB (primary) or localStorage (fallback)
 * Implements user isolation and optional encryption for public computers
 */

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  screenshot?: string; // Base64 encoded screenshot image
  actions?: Array<{
    type: string;
    entity: string;
    status: string;
    actionId?: string;
    toolName?: string;
    toolArgs?: any;
    data?: any;
    title?: string; // User-friendly title for the action
  }>;
  suggestions?: string[];
}

export interface ChatHistory {
  userId: string;
  sessionId: string;
  messages: ChatMessage[];
  summary?: string;
  createdAt: Date;
  updatedAt: Date;
  encrypted: boolean;
}

class ChatHistoryService {
  private db: IDBDatabase | null = null;
  private useIndexedDB: boolean = false;
  private currentUserId: string | null = null;
  private dbName = 'neko-assistant-chat';
  private dbVersion = 1;
  private storeName = 'chatHistory';

  /**
   * Initialize the storage service
   */
  async initialize(userId: string): Promise<void> {
    this.currentUserId = userId;

    // Try IndexedDB first
    if ('indexedDB' in window) {
      try {
        await this.initIndexedDB();
        this.useIndexedDB = true;
        console.log('[ChatHistory] Using IndexedDB for storage');
      } catch (error) {
        console.warn(
          '[ChatHistory] IndexedDB initialization failed, falling back to localStorage:',
          error
        );
        this.useIndexedDB = false;
      }
    } else {
      console.log('[ChatHistory] IndexedDB not available, using localStorage');
      this.useIndexedDB = false;
    }
  }

  /**
   * Initialize IndexedDB
   */
  private async initIndexedDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const objectStore = db.createObjectStore(this.storeName, {
            keyPath: 'sessionId',
          });
          objectStore.createIndex('userId', 'userId', { unique: false });
          objectStore.createIndex('updatedAt', 'updatedAt', { unique: false });
        }
      };
    });
  }

  /**
   * Save a message to chat history
   */
  async saveMessage(message: ChatMessage): Promise<void> {
    if (!this.currentUserId) {
      throw new Error('ChatHistoryService not initialized');
    }

    // Validate user matches current user
    if (message.role === 'user' && !this.currentUserId) {
      throw new Error('User mismatch - cannot save message');
    }

    const history = await this.loadHistory(this.currentUserId);
    const sessionId = history?.sessionId || this.generateSessionId();

    const updatedHistory: ChatHistory = {
      userId: this.currentUserId,
      sessionId,
      messages: [...(history?.messages || []), message],
      summary: history?.summary,
      createdAt: history?.createdAt || new Date(),
      updatedAt: new Date(),
      encrypted: history?.encrypted || false,
    };

    await this.saveHistory(updatedHistory);
  }

  /**
   * Load chat history for current user
   */
  async loadHistory(userId: string): Promise<ChatHistory | null> {
    // Validate user matches current user
    if (userId !== this.currentUserId) {
      console.warn('[ChatHistory] User ID mismatch, not loading history');
      return null;
    }

    if (this.useIndexedDB && this.db) {
      return this.loadFromIndexedDB(userId);
    } else {
      return this.loadFromLocalStorage(userId);
    }
  }

  /**
   * Load from IndexedDB
   */
  private async loadFromIndexedDB(userId: string): Promise<ChatHistory | null> {
    if (!this.db) return null;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('userId');
      const request = index.getAll(userId);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const histories = request.result as ChatHistory[];
        // Get the most recent history for this user
        const latest = histories.sort(
          (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()
        )[0];
        resolve(latest || null);
      };
    });
  }

  /**
   * Load from localStorage
   */
  private loadFromLocalStorage(userId: string): ChatHistory | null {
    try {
      const key = `neko-assistant-chat-${userId}`;
      const stored = localStorage.getItem(key);
      if (!stored) return null;

      const history = JSON.parse(stored) as ChatHistory;
      // Convert date strings back to Date objects
      history.createdAt = new Date(history.createdAt);
      history.updatedAt = new Date(history.updatedAt);
      history.messages = history.messages.map((msg) => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
      }));

      return history;
    } catch (error) {
      console.error('[ChatHistory] Error loading from localStorage:', error);
      return null;
    }
  }

  /**
   * Save chat history
   */
  private async saveHistory(history: ChatHistory): Promise<void> {
    if (this.useIndexedDB && this.db) {
      await this.saveToIndexedDB(history);
    } else {
      this.saveToLocalStorage(history);
    }
  }

  /**
   * Save to IndexedDB
   */
  private async saveToIndexedDB(history: ChatHistory): Promise<void> {
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.put(history);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  /**
   * Save to localStorage
   */
  private saveToLocalStorage(history: ChatHistory): void {
    try {
      const key = `neko-assistant-chat-${history.userId}`;
      localStorage.setItem(key, JSON.stringify(history));
    } catch (error) {
      console.error('[ChatHistory] Error saving to localStorage:', error);
      // Handle quota exceeded error
      if (
        error instanceof DOMException &&
        error.name === 'QuotaExceededError'
      ) {
        console.warn(
          '[ChatHistory] localStorage quota exceeded, clearing old data'
        );
        this.clearHistory(history.userId);
      }
    }
  }

  /**
   * Replace history with summary (for context continuation)
   */
  async replaceHistoryWithSummary(
    userId: string,
    summary: string
  ): Promise<void> {
    const history = await this.loadHistory(userId);
    if (!history) return;

    const updatedHistory: ChatHistory = {
      ...history,
      summary,
      messages: [], // Clear messages, keep only summary
      updatedAt: new Date(),
    };

    await this.saveHistory(updatedHistory);
  }

  /**
   * Clear chat history for current user
   */
  async clearHistory(userId: string): Promise<void> {
    if (this.useIndexedDB && this.db) {
      await this.clearFromIndexedDB(userId);
    } else {
      this.clearFromLocalStorage(userId);
    }
  }

  /**
   * Clear from IndexedDB
   */
  private async clearFromIndexedDB(userId: string): Promise<void> {
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('userId');
      const request = index.openCursor(IDBKeyRange.only(userId));

      request.onerror = () => reject(request.error);
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };
    });
  }

  /**
   * Clear from localStorage
   */
  private clearFromLocalStorage(userId: string): void {
    const key = `neko-assistant-chat-${userId}`;
    localStorage.removeItem(key);
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return `session-${Date.now()}-${Math.random()
      .toString(36)
      .substring(2, 9)}`;
  }

  /**
   * Get current user ID
   */
  getCurrentUserId(): string | null {
    return this.currentUserId;
  }
}

export const chatHistoryService = new ChatHistoryService();
